import DocumentCard from "@components/DocumentCard/DocumentCard";
import CustomFormLabel from "@components/FormItem/FormItem.styles";
import SideDrawer from "@components/SimpleSideDrawer/SideDrawer";
import { formatDateTime } from "@features/vessel-call/vessel-call-status/utils";
import { useDownloadCertificate } from "@pages/vessel-management/vessel-status/hooks/useDownloadCertificate";
import { TEST_ID_BASE } from "@pages/vessel-management/vessel-status/VesselStatus.tsx";
import { translateNormalizedRole } from "@utils/roles";
import { t } from "i18next";
import { LANG_BASE } from "../../ShippingAgencyTransfer";

type DetailsSideDrawerProps = {
  isOpen: boolean;
  testId: string;
  onClose: () => void;
  rejectionReason?: string;
  externalNotes?: string;
  externalDocument?: string;
  externalDocumentName?: string;
  internalNotes?: string;
  internalDocument?: string;
  internalDocumentName?: string;
  submittedBy?: string;
  submittedByRole?: string;
  submittedAt?: string;
};

const service = "bff-vessel-call";

const DetailsSideDrawer: React.FC<DetailsSideDrawerProps> = ({
  isOpen,
  testId,
  onClose,
  rejectionReason,
  externalNotes,
  externalDocument,
  externalDocumentName,
  internalNotes,
  internalDocument,
  internalDocumentName,
  submittedBy,
  submittedByRole,
  submittedAt,
}) => {
  const { handleDownload, handlePreview } = useDownloadCertificate();
  const submittedByName = submittedBy;
  const submittedByRoleText = translateNormalizedRole(t, submittedByRole);
  const hasSubmittedByInfo = Boolean(submittedByName || submittedByRoleText);
  const submittedAtText = submittedAt ? formatDateTime(submittedAt) : "-";

  return (
    <div data-testid={`${testId}-container`} className="min-w-[400px]">
      <SideDrawer
        isOpen={isOpen}
        title={t(`${LANG_BASE}.status-drawer-title`)}
        onClose={onClose}
        submitLabel={t("common.ok")}
        onSubmit={onClose}
        hideCancelButton={true}
      >
        <div className="flex flex-col gap-4 w-[400px] p-6">
          {rejectionReason && (
            <div className="col-span-full flex flex-col gap-2">
              <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }} data-testid="labelId">
                {t(`${LANG_BASE}.rejection-reason`)}
              </CustomFormLabel>
              <div
                className="text-sm whitespace-pre-wrap wrap-break-word"
                data-testid={`${testId}-static-content`}
              >
                {rejectionReason}
              </div>
            </div>
          )}
          {externalNotes && (
            <div className="col-span-full flex flex-col gap-2">
              <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }} data-testid="labelId">
                {t(`${LANG_BASE}.external-notes`)}
              </CustomFormLabel>
              <div
                className="text-sm whitespace-pre-wrap wrap-break-word"
                data-testid={`${testId}-static-content`}
              >
                {externalNotes}
              </div>
            </div>
          )}
          {externalDocument && (
            <div className="col-span-full mb-2 flex flex-col gap-2">
              <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }}>
                {t(`${LANG_BASE}.attachments`)}
              </CustomFormLabel>
              <DocumentCard
                data-testid={`${testId}-external-attachment`}
                documentName={externalDocumentName}
                fileName=""
                onDownload={() => {
                  handleDownload("document", service, externalDocument, externalDocumentName);
                }}
                onPreview={() => handlePreview("document", service, externalDocument)}
              />
            </div>
          )}
          {internalNotes && (
            <div className="col-span-full mb-2 flex flex-col gap-2">
              <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }} data-testid="labelId">
                {t(`${LANG_BASE}.internal-notes`)}
              </CustomFormLabel>
              <p
                className="text-sm whitespace-pre-wrap wrap-break-word"
                data-testid={`${TEST_ID_BASE}-static-content`}
              >
                {internalNotes}
              </p>
            </div>
          )}
          {internalDocument && (
            <div className="col-span-full mb-2 flex flex-col gap-2">
              <CustomFormLabel sx={{ fontSize: 16, fontWeight: 700 }}>
                {t(`${LANG_BASE}.internal-attachments`)}
              </CustomFormLabel>
              <DocumentCard
                data-testid={`${testId}-internal-attachment`}
                documentName={internalDocumentName}
                fileName=""
                onDownload={() => {
                  handleDownload("document", service, internalDocument, internalDocumentName);
                }}
                onPreview={() => handlePreview("document", service, internalDocument)}
              />
            </div>
          )}
          <div>
            <p className="text-xs text-gray-500">
              <span>{t("vessel-call.interruption.submittedBy")}:</span>{" "}
              {hasSubmittedByInfo ? (
                <>
                  {submittedByName && <span className="text-gray-800">{submittedByName}</span>}
                  {submittedByName && submittedByRoleText ? " / " : null}
                  {submittedByRoleText}
                </>
              ) : (
                "-"
              )}
            </p>
            <p className="text-xs text-gray-500 mt-1">{submittedAtText}</p>
          </div>
        </div>
      </SideDrawer>
    </div>
  );
};

export default DetailsSideDrawer;
